import React, { useState, useMemo } from 'react';
import {
  TextInput, SimpleGrid, Tooltip, Box, Text, Popover, UnstyledButton, Group, Button,
} from '@mantine/core';
import * as TablerIcons from '@tabler/icons-react';
import { searchIcons } from './iconCatalog';

function IconTile({ name, isSelected, onSelect }) {
  const Icon = TablerIcons[name];
  if (!Icon) return null;
  return (
    <Tooltip label={name} withArrow>
      <UnstyledButton
        onClick={() => onSelect(name)}
        aria-label={name}
        style={{
          padding: 8,
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isSelected ? 'var(--mantine-color-editor-1)' : 'transparent',
          border: isSelected ? '2px solid var(--mantine-color-editor-5)' : '2px solid transparent',
        }}
      >
        <Icon size={18} stroke={1.5} color={isSelected ? 'var(--mantine-color-editor-7)' : undefined} />
      </UnstyledButton>
    </Tooltip>
  );
}

/**
 * Champ Puck de sélection d'icône Tabler (valeur = nom string, ex "IconStar").
 * Compact : une ligne icône courante + « Modifier » ; la grille de recherche
 * (mots-clés français, catalogue curé + pack complet) s'ouvre en popover.
 */
const IconPickerField = ({ value, onChange }) => {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');

  const results = useMemo(() => searchIcons(search), [search]);
  const CurrentIcon = value ? TablerIcons[value] : null;

  const handleSelect = (name) => {
    onChange(name);
    setOpened(false);
    setSearch('');
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={280}
      position="bottom-start"
      shadow="md"
      withinPortal
      trapFocus
    >
      <Popover.Target>
        <UnstyledButton
          onClick={() => setOpened((o) => !o)}
          aria-expanded={opened}
          style={{
            width: '100%',
            padding: '6px 8px',
            borderRadius: 6,
            border: '1px solid var(--mantine-color-gray-4)',
            background: 'var(--mantine-color-white)',
          }}
        >
          <Group justify="space-between" wrap="nowrap" gap="xs">
            <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
              {CurrentIcon ? <CurrentIcon size={20} stroke={1.5} /> : null}
              <Text size="xs" c={value ? undefined : 'dimmed'} truncate>
                {value ? value.replace(/^Icon/, '') : 'Aucune icône'}
              </Text>
            </Group>
            <Text size="xs" c="editor" fw={600} style={{ flexShrink: 0 }}>
              Modifier
            </Text>
          </Group>
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown p="xs">
        <TextInput
          placeholder="Rechercher (ex : équipe, horloge…)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="xs"
          mb="xs"
          data-autofocus
        />
        <Box style={{ maxHeight: 220, overflowY: 'auto' }}>
          {results.length === 0 ? (
            <Text size="xs" c="dimmed" ta="center" py="md">Aucune icône trouvée</Text>
          ) : (
            <SimpleGrid cols={6} spacing={4}>
              {results.map((name) => (
                <IconTile
                  key={name}
                  name={name}
                  isSelected={value === name}
                  onSelect={handleSelect}
                />
              ))}
            </SimpleGrid>
          )}
        </Box>
        {value && (
          <Group justify="space-between" mt="xs">
            <Text size="xs" c="dimmed" truncate>Sélectionné : {value}</Text>
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={() => handleSelect('')}
            >
              Retirer
            </Button>
          </Group>
        )}
      </Popover.Dropdown>
    </Popover>
  );
};

export default IconPickerField;
